/**
 * PATH: packages/ui/src/kanban/components/KanbanCardAllocateBoards.tsx
 * FILE_ID: UI-KANBAN-CARD-ALLOCATE-BOARDS-001
 * PURPOSE: Boards listed under the A menu lanes so the card can move.
 * ROLE IN ARCHITECTURE: Presentational leaf under the card quick-action popover.
 * MAIN EXPORTS: KanbanCardAllocateBoards
 * NON-RESPONSIBILITIES: Does not link the card onto another lane of this board.
 * NOTES FOR FUTURE AI: This is a move. The card leaves the current board.
 *   Also-show on a lane stays in the lane list above.
 */
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

import type { Board, Card } from '../types'

const ROW =
  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-100 disabled:opacity-50 dark:hover:bg-neutral-900'

export function KanbanCardAllocateBoards({
  card,
  busy,
  currentBoardId,
  onMoveToBoard,
  onLoadBoards,
  run,
}: {
  card: Card
  busy: boolean
  currentBoardId?: string | null
  onMoveToBoard?: (card: Card, board: Board) => Promise<unknown> | void
  onLoadBoards?: () => Promise<Board[]>
  run: (fn: () => Promise<unknown> | void) => void
}) {
  const [boards, setBoards] = useState<Board[] | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!onLoadBoards || !onMoveToBoard) return
    let cancelled = false
    setLoading(true)
    void onLoadBoards()
      .then((loaded) => {
        if (cancelled) return
        setBoards(loaded.filter((board) => board.id !== currentBoardId))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentBoardId, onLoadBoards, onMoveToBoard])

  if (!onLoadBoards || !onMoveToBoard) return null

  return (
    <div className="border-t border-neutral-100 dark:border-neutral-800" data-testid="kanban-quick-move-boards">
      <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
        Move to board
      </div>
      {loading && boards === null && (
        <div className="px-3 py-2 text-xs text-neutral-400">
          <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
          Loading boards
        </div>
      )}
      {(boards ?? []).map((board) => (
        <button
          key={board.id}
          type="button"
          className={ROW}
          disabled={busy}
          data-testid={`kanban-quick-move-board-${board.id}`}
          onClick={() => run(() => onMoveToBoard(card, board))}
        >
          <span className="truncate">{board.name}</span>
        </button>
      ))}
      {!loading && boards?.length === 0 && (
        <div className="px-3 py-2 text-xs text-neutral-400">No other boards.</div>
      )}
    </div>
  )
}
